import {
  areIntervalsOverlapping,
  format,
  formatISO,
  Interval,
  isAfter as isAfterFns,
  isBefore as isBeforeFns,
  isEqual,
  max,
  min,
  parse,
} from 'date-fns';

import { ensure } from '../types';

const DAY_IN_MS = 1000 * 60 * 60 * 24;

export function formatDate(candidate?: Date): string {
  if (!candidate) return '';

  return format(candidate, 'dd-MMM-yyyy');
}

export function formatDateWithCustomFormat(candidate: Date, dateFormat: string): string {
  return format(candidate, dateFormat);
}

export function formatDateTime(candidate?: Date): string {
  if (!candidate) return '';

  return format(candidate, 'dd-MMM-yyyy HH:mm:ss');
}

export function formatDateFromString(candidate?: string): string {
  if (!candidate) return '';

  try {
    const output = parse(candidate, 'yyyy-MM-dd', new Date());

    return formatDate(output);
  } catch {
    return 'Invalid date string';
  }
}
export function getMinDate(dates: Date[]): Date | undefined {
  if (dates.length === 0) {
    return undefined;
  }

  return min(dates);
}

export function getMaxDate(dates: Date[]): Date | undefined {
  if (dates.length === 0) {
    return undefined;
  }

  return max(dates);
}

export function intervalsOverlap(intervalLeft: Interval, intervalRight: Interval): boolean {
  try {
    return areIntervalsOverlapping(intervalLeft, intervalRight, {
      inclusive: true,
    });
  } catch {
    return false;
  }
}

export function timestampToSeconds(timestamp: string) {
  const pv = timestamp.split(':').map(Number);

  return pv[0] * 360000 + pv[1] * 60000 + pv[2] * 1000;
}

export function getJsonSchemaDate(candidate?: Date) {
  return candidate ? formatISO(candidate, { representation: 'date' }) : '';
}

export function getJsonSchemaDateTime(candidate?: Date) {
  return getFullIsoDate(candidate);
}

export function getFullIsoDate(candidate: Date = new Date()) {
  return formatISO(candidate, { representation: 'complete' });
}

export function getLocalDateRequired(candidate: string | Date): Date {
  const date = getLocalDate(candidate);
  ensure(date);

  return date;
}

export function getLocalDate(candidate?: string | Date): Date | undefined {
  if (!candidate) {
    return undefined;
  }

  const date = new Date(candidate);

  const dateHours = date.getHours();
  const timezoneOffsetHours = date.getTimezoneOffset() / 60;
  const absoluteTimezoneOffsetHours = Math.abs(timezoneOffsetHours);

  const shouldCorrectTimezonOffset = dateHours > absoluteTimezoneOffsetHours;
  if (!shouldCorrectTimezonOffset) return date;

  const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);

  return localDate;
}

export function getLocalDateFormatted(candidate?: string | Date) {
  return formatDate(getLocalDate(candidate));
}

export function isBefore(first: Date | number, second: Date | number) {
  return isBeforeFns(first, second);
}

export function isAfter(first: Date | number, second: Date | number) {
  return isAfterFns(first, second);
}

export function areDatesEqual(first: Date | number, second: Date | number) {
  return isEqual(first, second);
}

export function getCosmosIsoDate(date: Date) {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSSSSS'Z'");
}

export const formatDateString = (date: string): string => {
  return formatDate(new Date(date));
};

export const resetTimePartOfDate = (date: Date | string) => {
  const dateWithResettedTime = new Date(date);
  const timezoneOffset = dateWithResettedTime.getTimezoneOffset() / 60;

  dateWithResettedTime.setHours(dateWithResettedTime.getHours() + timezoneOffset);

  return dateWithResettedTime;
};

export const getEarliestDate = (dates: (Date | string)[]) => {
  const sortedDates = dates.map(date => getLocalDate(date)!.getTime()).sort();

  return new Date(sortedDates[0]);
};

export const getLatestDate = (dates: (Date | string)[]) => {
  const sortedDates = dates.map(date => getLocalDate(date)!.getTime()).sort();

  return new Date(sortedDates[sortedDates.length - 1]);
};

export const getJsonSchemaLocalDate = (date: Date | string) => getJsonSchemaDate(getLocalDate(date));

export const getEarliestSystemStartDate = (allStartDates: (string | Date)[]) =>
  getJsonSchemaLocalDate(getEarliestDate(allStartDates));

export const getLatestSystemEndDate = (allEndDates: (string | Date)[]) =>
  getJsonSchemaLocalDate(getLatestDate(allEndDates));

const getDateWithDateOfMonthOverride = (date: Date, dateOfMonthOverride: number): Date => {
  const newDate = new Date(date);

  newDate.setDate(dateOfMonthOverride);

  return newDate;
};

export const subtractDaysFromDate = (date: Date, amountOfDays: number): Date => {
  return getDateWithDateOfMonthOverride(date, date.getDate() - amountOfDays);
};

export const addDaysToDate = (date: Date, amountOfDays: number): Date => {
  return getDateWithDateOfMonthOverride(date, date.getDate() + amountOfDays);
};

export const getDayDifference = (date: Date, dateToCompare: Date): number => {
  const dateMs = date.getTime();
  const dateToCompareMs = dateToCompare.getTime();

  const differenceInMs = dateToCompareMs > dateMs ? dateToCompareMs - dateMs : dateMs - dateToCompareMs;

  return Math.floor(differenceInMs / DAY_IN_MS);
};

export const isValidDate = (date: Date | string): boolean => {
  return !Number.isNaN(new Date(date).getTime());
};
